import { ReactElement, useMemo } from 'react';

import { FormControl, FormField, FormItem, FormMessage } from '@crossnokaye/ux-common';

import { SettingFieldProps } from '@/features/equipment-browser/equipment-details/settings/components/setting-field.types';
import { getBooleanSettingVocabulary } from '@/features/equipment-browser/equipment-details/settings/components/setting-field-boolean.utils';
import { SettingFieldLabelRow } from '@/features/equipment-browser/equipment-details/settings/components/setting-field-label-row';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { InputValueChange } from '@/ux-common/components/input/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/ux-common/components/select/select';
import { withTooltip } from '@/ux-common/components/tooltip/with-tooltip';

const SelectTriggerWithTooltip = withTooltip(SelectTrigger);

/**
 * Boolean setting, drawn as a two-option dropdown so every field on the Settings
 * page is the same control.
 *
 * The two options are the setting's own vocabulary — ON / OFF by default, or
 * OPEN / CLOSED where the setting says so — and that label is also what the form
 * stores, so the field and the save dialog describe a change the same way. See
 * `setting-field-boolean.utils`.
 */
export const SettingFieldBoolean = ({ form, name, setting }: SettingFieldProps): ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  // Scoped dirty-state subscription. See `useFieldIsDirty` for why this is
  // form-level `dirtyFields` rather than `fieldState.isDirty`, and why we
  // scope by name rather than read the whole dirty map.
  const hasChanged = useFieldIsDirty(form.control, name);

  // See `setting-field-boolean.utils` for why the Select's Off / On vocabulary and
  // the setting's stored label are kept apart, and converted only at the edges.
  const { offLabel, onLabel, fromStored } = useMemo(
    () => getBooleanSettingVocabulary(setting),
    [setting]
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const selected = fromStored(field.value);
        const original = fromStored(setting.value);

        // One copy of the children for both branches below. A fragment is safe:
        // the trigger looks through fragments when it hoists the value-change
        // lockup out of the truncating value span.
        const triggerChildren = (
          <>
            {selected || undefined}
            {selected && hasChanged && original ? (
              <InputValueChange oldValue={original} />
            ) : undefined}
          </>
        );

        return (
          <FormItem data-component="SettingFieldBoolean">
            <Select
              disabledReason={disabledByPermissions ? tooltipText : undefined}
              onValueChange={(next) => {
                // Only the two labels this setting offers. Anything else — a stray
                // value from autofill or restored state — is ignored rather than
                // resolved, which would stage a value the operator never chose.
                if (next !== offLabel && next !== onLabel) return;
                field.onChange(next);
              }}
              state={hasChanged ? 'new' : undefined}
              value={selected}
            >
              <SettingFieldLabelRow description={setting.description} label={setting.label} />
              <FormControl>
                {/* Still wrapped unconditionally: `withTooltip` keeps the trigger
                    outside the branch that toggles the content, so the reason
                    appearing or clearing never remounts it. The reason also reaches
                    assistive tech through the root's `disabledReason`, which does
                    not depend on hover. */}
                <SelectTriggerWithTooltip
                  // `FormControl` always emits `aria-describedby`, pointing at
                  // `formDescriptionId` — and no `FormDescription` is rendered here
                  // or anywhere in gaia, so it resolves to nothing. Passing
                  // undefined lets the group supply its own: the refusal reason and
                  // any `InputHelper`, which are real. Child props win over the
                  // Slot's.
                  aria-describedby={undefined}
                  placeholder={'Select an option'}
                  tooltipArrow
                  tooltipContent={disabledByPermissions ? tooltipText : undefined}
                >
                  {triggerChildren}
                </SelectTriggerWithTooltip>
              </FormControl>
              <SelectContent>
                <SelectItem value={offLabel}>{offLabel}</SelectItem>
                <SelectItem value={onLabel}>{onLabel}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
